/**
 * The readers that the hand-written body parsers of `src/api` share. `parseAppFleetSummary` and
 * `parseOrgAppLicences` use the `wire*` readers: each returns the value with its wire type, or
 * throws the error that the parser's `fault` makes from the field name. The root-surface read of
 * `shell.api` imports only `isRecord`, and returns its own `none` result on a bad shape. No reader
 * repeats a value from the body.
 */

/** Makes the rejection of one parser from the name of the field that failed. */
export type ShapeFault = (field: string) => Error;

/** `true` for an object that is no array, so a field read on it is safe. */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The value as a string, or the rejection that `fault` makes for `field`. */
export function wireText(value: unknown, field: string, fault: ShapeFault): string {
  if (typeof value !== 'string') throw fault(field);
  return value;
}

/** The value as a string or `null`, or the rejection that `fault` makes for `field`. */
export function wireTextOrNull(value: unknown, field: string, fault: ShapeFault): string | null {
  return value === null ? null : wireText(value, field, fault);
}

/** The value as an object that is no array, or the rejection that `fault` makes for `field`. */
export function wireRecord(value: unknown, field: string, fault: ShapeFault): Record<string, unknown> {
  if (!isRecord(value)) throw fault(field);
  return value;
}

/** The value as an array, or the rejection that `fault` makes for `field`. */
export function wireList(value: unknown, field: string, fault: ShapeFault): readonly unknown[] {
  if (!Array.isArray(value)) throw fault(field);
  return value;
}
